import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateForm } from '@assets/js/util/formValidation.js';
import { formAtt, isEmpty } from '@assets/js/util/commonUtil.js';
import CompFmtInput from '@components/form/fragment/comp-fmt-input.js';
import CompFmtFile from '@components/form/fragment/comp-fmt-file.js';
import {ApiSearch, ApiSaveForm} from '@assets/js/api/commonApi.js';

// 로딩 스피너 컴포넌트
const LoadingOverlay = ({ message }) => (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
    }}>
        <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ color: 'white', marginTop: '1rem', fontSize: '1.1rem' }}>{message}</p>
    </div>
);

const CompFormMatchResultAward = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [title] = useState('상장');
    const [deleteFileList, setDeleteFileList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheetAward';
    const getValidateOpt = () => ({
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['contentTxt', 'comptnDt', 'userNm', 'userPsNm'],
        params: params,
    });

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            setParams(p_params);
            setDeleteFileList([]); // 삭제 목록 초기화
            openModal();
            handleToggle();
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const openModal = () => {
        onSearch();
    }

    const handleToggle = () => {
        const targetEle = document.querySelector('#' + rootId);
        if (targetEle) {
            targetEle.style.display = targetEle.style.display === 'none' ? 'block' : 'none';
        }
    }

    //상장 폼 정보 조회.
    const onSearch = () => {
        ApiSearch('/sys/matchresultmng/award/search', {})
        .then(res => {
            if (res && res.success) {
                let atchFileId = '';
                let mode = 'I';
                if (res.data.data != null) {
                    mode = 'M';
                    atchFileId = res.data.data.atchFileId;
                }

                let contentTxt = "위 선수는 대한팔씨름연맹 경기지부 경기도팀이 주최 주관하는<제1회 경기지부 팔씨름 챔피언십> 에서 위와 같이 우수한 성적으로 입상하였기에 본 상장을 수여합니다.";
                if (!isEmpty(res.data.data) && !isEmpty(res.data.data.contentTxt)) {
                    contentTxt = res.data.data.contentTxt;
                }

                setParams({
                    ...res.data.data, 
                    mode: mode,
                    atchFileId_reportFile : atchFileId,
                    contentTxt: contentTxt
                });
            }
        });
    }
    
    //상장 폼 정보 저장 - file포함.
    const onSaveForm = () => {
        if (!validateForm(getValidateOpt())) { return; }

        // uploadType 파라미터 추가
        const formParams = {
            ...params,
            uploadType: 'report'
        };

        // deleteFileList가 있으면 추가
        if (deleteFileList.length > 0) {
            formParams.deleteFileList = JSON.stringify(deleteFileList);
        }

        // 로딩 표시 시작
        setIsLoading(true);
        setLoadingMessage('상장 정보를 저장하는 중...');

        ApiSaveForm('/sys/matchresultmng/award/save', formParams).then(res => {
            if (res && res.success) {
                setLoadingMessage('저장 완료!');

                // 짧은 딜레이 후 로딩 종료
                setTimeout(() => {
                    setIsLoading(false);
                    setLoadingMessage('');

                    // 삭제 목록 초기화
                    setDeleteFileList([]);
                    // Close the modal after saving
                    handleToggle();
                    // Search for updated data
                    props.onGridSearch();
                }, 500);
            } else {
                setIsLoading(false);
                setLoadingMessage('');
            }
        }).catch(error => {
            setIsLoading(false);
            setLoadingMessage('');
            console.error('저장 중 오류 발생:', error);
        });
    }

    const onValChange = (e) => {
        const { id } = e.target;
        // Validate the specific input field
        validateForm(getValidateOpt(), id);
    }

    const onInputChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }

    const onFileChange = (e) => {
        const { id, value } = e.target;
        // 파일 객체를 params에 저장
        setParams({ ...params, [id]: value });
    }

    const onDeleteFileHandler = (deleteFileInfo) => {
        // 삭제 목록에 추가
        setDeleteFileList([deleteFileInfo]);
    }

    /****************************
     * Define form config
    *****************************/
    const formObj = {
        contentTxt : formAtt({id: 'contentTxt', label: '내용', value: params['contentTxt'] || '', required: true, classCol: 'col-12'}),
        comptnDt : formAtt({id: 'comptnDt', label: '대회일자', value: params['comptnDt'] || '', placeholder: "예: 20XX-01-01(월)", required: true, classCol: 'col-12'}),
        userNm : formAtt({id: 'userNm', label: '대표자', value: params['userNm'] || '', placeholder: "예: 홍 길 동", required: true, classCol: 'col-6'}),
        userPsNm : formAtt({id: 'userPsNm', label: '직함', value: params['userPsNm'] || '', placeholder: "예: 지부장", required: true, classCol: 'col-6'}),
        reportFile : formAtt({id: 'reportFile', label: '상장 헤더 이미지', classCol: 'col-12', accept: 'image/*'}),
    }

    return (
        <>
            {/* 로딩 오버레이 */}
            {isLoading && <LoadingOverlay message={loadingMessage} />}

            <div
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-md">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-5">{title}</h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            {/* Tournament specific fields */}
                                            <div className="p-3 rounded">
                                                <div className="row mb-2">
                                                    <label htmlFor="contentTxt" className="form-label">{formObj.contentTxt.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="contentTxt"
                                                        rows="4"
                                                        value={params.contentTxt || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.comptnDt}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.userPsNm}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                    <CompFmtInput
                                                        attrs={formObj.userNm}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                                <div className="row ">
                                                    <CompFmtFile
                                                        attrs={formObj.reportFile}
                                                        onChange={onFileChange}
                                                        delConfirmMsg="삭제하시겠습니까? 상장 생성시 기본 이미지로 대체 됩니다."
                                                        atchFileId={params.atchFileId}
                                                        atchFileSn={params.atchFileSn}
                                                        atchFileNm={params.atchFileNm}
                                                        filePathNm={params.filePathNm}
                                                        onDeleteFile={onDeleteFileHandler}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0">
                            <div className="btn-group">
                                <button type="button" className="btn btn-lg btn-primary" onClick={onSaveForm}>
                                    Save changes
                                </button>
                                <button type="button" className="btn btn-lg btn-secondary" data-bs-dismiss="modal" onClick={handleToggle}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CompFormMatchResultAward;
